<template>
    <div>
        <div class="dashboard-container" :class="{'dark': theme === THEMES.DARK}">
            <section v-if="!canViewDashboard" class="dashboard-welcome" aria-label="Signed in">
                <h1>You're signed in as {{ signedInName }}.</h1>
                <p>Your 4-digit passcode is ready to use on the POS tablet. Ask your manager if you need dashboard access.</p>
            </section>
            <div v-else class="dashboard-content">
                <!-- Dashboard Header with date controls and refresh functionality -->
                <DashboardHeader
                    :selected-date="selectedDateRange"
                    :last-updated-time="lastUpdatedTime"
                    :auto-refresh-enabled="autoRefreshEnabled"
                    :custom-date-range="customDateRange"
                    @update-date="updateDateRange"
                    @apply-custom-date="applyCustomDateRange"
                    @refresh="refreshDashboard"
                    @toggle-auto-refresh="toggleAutoRefresh"
                />

                <ActionRequiredPanel
                    :items="actionItems"
                    :is-dark="theme === THEMES.DARK"
                    :loading="actionItemsLoading"
                />

                <!-- Stats Cards Grid -->
                <div class="stats-grid">
                    <!-- Revenue Card -->
                    <RevenueCard
                        :value="dashboardStats && dashboardStats.todayRevenue"
                        :date-range="selectedDateRange"
                        :stats-comparison="statsComparison.revenue"
                        :comparison-period="comparisonPeriod"
                    />

                    <!-- Orders Card -->
                    <OrdersCard
                        :value="dashboardStats && dashboardStats.todayOrders"
                        :date-range="selectedDateRange"
                        :stats-comparison="statsComparison.orders"
                        :comparison-period="comparisonPeriod"
                    />

                    <!-- Average Order Value Card -->
                    <AverageOrderValueCard
                        :value="dashboardStats && dashboardStats.averageOrderValue"
                        :stats-comparison="statsComparison.averageOrderValue"
                        :comparison-period="comparisonPeriod"
                    />

                    <!-- Products Card -->
                    <ProductsCard
                        :value="dashboardStats && dashboardStats.totalProducts"
                        :stats-comparison="statsComparison.products"
                        :comparison-period="comparisonPeriod"
                    />
                </div>

                <!-- Charts Section -->
                <div class="charts-section">
                    <RevenueOverTimeChart 
                        :is-dark="theme === THEMES.DARK"
                        :initial-date-range="selectedDateRange"
                        :custom-date-range="customDateRange"
                        :include-pilot-sales="includePilotSales"
                        ref="revenueChart"
                    />
                    
                    <div class="sub-charts-section">
                        <SalesByWarehouseChart
                            :is-dark="theme === THEMES.DARK"
                            :initial-date-range="selectedDateRange"
                            :custom-date-range="customDateRange"
                            :include-pilot-sales="includePilotSales"
                            @data-loaded="handleWarehouseSalesLoaded"
                            ref="warehouseChart"
                        />
                        <SalesByProductChart
                            :is-dark="theme === THEMES.DARK"
                            :initial-date-range="selectedDateRange" 
                            :custom-date-range="customDateRange"
                            :include-pilot-sales="includePilotSales"
                            @data-loaded="handleProductSalesLoaded"
                            ref="productChart"
                        />
                        <TopBrandSalesChart
                            class="top-brand-chart"
                            :is-dark="theme === THEMES.DARK"
                            :initial-date-range="selectedDateRange"
                            :custom-date-range="customDateRange"
                            :include-pilot-sales="includePilotSales"
                            ref="brandChart"
                        />
                    </div>
                </div>

                <!-- AGCO compliance submission health -->
                <div class="agco-health-section">
                    <AgcoHealthCard
                        :is-dark="theme === THEMES.DARK"
                        @data-loaded="handleAgcoHealthLoaded"
                        ref="agcoHealthCard"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import millify from "millify"
import { canViewDashboard } from "../utils/dashboard-access"
import { mapState, mapActions } from "vuex"

// Dashboard Components
import DashboardHeader from "../components/dashboard/DashboardHeader.vue"
import ActionRequiredPanel from "../components/dashboard/ActionRequiredPanel.vue"
import ProductsCard from "../components/dashboard/ProductsCard.vue"
import OrdersCard from "../components/dashboard/OrdersCard.vue"
import RevenueCard from "../components/dashboard/RevenueCard.vue"
import AverageOrderValueCard from "../components/dashboard/AverageOrderValueCard.vue"
import AgcoHealthCard from "../components/dashboard/AgcoHealthCard.vue"
import {
    buildDashboardActionItems,
    formatDashboardCurrency,
    formatDashboardInteger,
    isDashboardActionItemsLoading,
    normalizeDashboardCheckPayload,
    resetDashboardSalesScope,
    storeHasQaSeededAgcoEvent
} from "../utils/dashboard-action-items"
import {
    formatBusinessDateRangeLabel,
    formatBusinessInstant,
    getBusinessDateRangeForPreset,
    resolveDashboardDateRange
} from "../utils/business-date"

// Chart Components
import RevenueOverTimeChart from "../components/charts/RevenueOverTimeChart.vue"
import SalesByWarehouseChart from "../components/charts/SalesByWarehouseChart.vue"
import SalesByProductChart from "../components/charts/SalesByProductChart.vue"
import TopBrandSalesChart from "../components/charts/TopBrandSalesChart.vue"

export default {
    name: "Dashboard",
    components: { 
        DashboardHeader,
        ActionRequiredPanel,
        ProductsCard,
        OrdersCard,
        RevenueCard,
        AverageOrderValueCard,
        AgcoHealthCard,
        RevenueOverTimeChart,
        SalesByWarehouseChart,
        SalesByProductChart,
        TopBrandSalesChart
    },
    data() {
        const initialRange = getBusinessDateRangeForPreset('last7days');
        return {
            loading: false,
            selectedDateRange: 'last7days', // Default to Last 7 days
            comparisonPeriod: 'previous_period',
            dateRange: {
                startDate: initialRange.start,
                endDate: initialRange.end
            },
            customDateRange: {
                start: initialRange.start,
                end: initialRange.end
            },
            lastUpdatedTime: formatBusinessInstant(new Date(), { timeOnly: true }),
            autoRefreshEnabled: false, // Default to disabled
            statsComparison: {
                products: { percentage: 0, trend: 'neutral' },
                customers: { percentage: 0, trend: 'neutral' },
                orders: { percentage: 0, trend: 'neutral' },
                revenue: { percentage: 0, trend: 'neutral' },
                averageOrderValue: { percentage: 0, trend: 'neutral' }
            },
            dashboardPeriods: {
                current: {},
                previous: {}
            },
            salesByWarehouseSnapshot: null,
            topProductsSnapshot: null,
            agcoHealthSnapshot: null,
            dashboardStatsCheckComplete: false,
            dashboardStatsRequestId: 0,
            dashboardCheckErrors: {
                stats: false,
                warehouseSales: false,
                productSales: false,
                agcoHealth: false
            }
        }
    },
    computed: {
        ...mapState("misc", ["dashboardStats", "theme", "THEMES"]),
        ...mapState("auth", ["user"]),
        canViewDashboard() {
            return canViewDashboard(this.user)
        },
        signedInName() {
            return [this.user?.first_name, this.user?.last_name].filter(Boolean).join(" ") || this.user?.username || "staff"
        },
        includePilotSales() {
            return Boolean(this.user && this.user.isAdmin && this.$route && this.$route.query.includePilotSales === "true")
        },
        formattedDateRange() {
            return formatBusinessDateRangeLabel(this.dateRange.startDate, this.dateRange.endDate);
        },
        actionItems() {
            return buildDashboardActionItems(this);
        },
        actionItemsLoading() {
            return isDashboardActionItemsLoading(this);
        }
    },
    watch: {
        canViewDashboard(allowed) {
            if (allowed) {
                this.updateDateRange(this.selectedDateRange);
            } else {
                this.stopAutoRefresh();
                this.dashboardStatsRequestId++;
            }
        },
        includePilotSales() {
            if (!this.canViewDashboard) return;
            resetDashboardSalesScope(this);
        }
    },
    mounted() {
        if (!this.canViewDashboard) return;
        this.updateDateRange('last7days');
        
        // Auto-refresh is now disabled by default
        if (this.autoRefreshEnabled) {
            this.startAutoRefresh();
        }
    },
    beforeDestroy() {
        this.stopAutoRefresh();
    },
    methods: {
        millify,
        ...mapActions("misc", ["setData", "refreshDashboardStats"]),
        formatCurrency(value) {
            return formatDashboardCurrency(value);
        },
        formatInteger(value) {
            return formatDashboardInteger(value);
        },
        normalizeCheckPayload(payload) {
            return normalizeDashboardCheckPayload(payload);
        },
        handleWarehouseSalesLoaded(payload) {
            const result = this.normalizeCheckPayload(payload);
            this.salesByWarehouseSnapshot = result.rows;
            this.dashboardCheckErrors.warehouseSales = result.error;
        },
        handleProductSalesLoaded(payload) {
            const result = this.normalizeCheckPayload(payload);
            this.topProductsSnapshot = result.rows;
            this.dashboardCheckErrors.productSales = result.error;
        },
        handleAgcoHealthLoaded(payload) {
            const result = this.normalizeCheckPayload(payload);
            this.agcoHealthSnapshot = result.rows;
            this.dashboardCheckErrors.agcoHealth = result.error;
        },
        storeHasQaSeededAgcoEvent(store) {
            return storeHasQaSeededAgcoEvent(store);
        },

        updateDateRange(range) {
            this.selectedDateRange = range;
            const rangeDates = getBusinessDateRangeForPreset(range);

            this.dateRange = {
                startDate: rangeDates.start,
                endDate: rangeDates.end
            };
            
            // Convert the dates to string format for custom range
            this.customDateRange = {
                start: this.dateRange.startDate,
                end: this.dateRange.endDate
            };
            
            // Reuse this captured business range across every dashboard request.
            this.fetchDashboardData(this.dateRange);
            
            // Update all charts with the new date range
            this.updateAllCharts(range, this.customDateRange);
        },
        
        updateAllCharts(range, customDateRange = this.customDateRange) {
            this.salesByWarehouseSnapshot = null;
            this.topProductsSnapshot = null;
            this.agcoHealthSnapshot = null;
            this.dashboardCheckErrors.warehouseSales = false;
            this.dashboardCheckErrors.productSales = false;
            this.dashboardCheckErrors.agcoHealth = false;

            // Update the revenue chart
            if (this.$refs.revenueChart) {
                this.$refs.revenueChart.setDateRange(range, customDateRange);
            }
            
            // Update the warehouse chart
            if (this.$refs.warehouseChart) {
                this.$refs.warehouseChart.setDateRange(range, customDateRange);
            }
            
            // Update the product chart
            if (this.$refs.productChart) {
                this.$refs.productChart.setDateRange(range, customDateRange);
            }

            if (this.$refs.brandChart) {
                this.$refs.brandChart.setDateRange(range, customDateRange);
            }

            if (this.$refs.agcoHealthCard) {
                this.$refs.agcoHealthCard.fetchHealth();
            }
        },
        hasDashboardStatsChanged(nextStats) {
            const currentStats = this.dashboardStats || {}
            return Object.keys(nextStats).some((key) => nextStats[key] !== currentStats[key])
        },
        resetDashboardDataFallback() {
            this.statsComparison = {
                products: { percentage: 0, trend: 'neutral' },
                customers: { percentage: 0, trend: 'neutral' },
                orders: { percentage: 0, trend: 'neutral' },
                revenue: { percentage: 0, trend: 'neutral' },
                averageOrderValue: { percentage: 0, trend: 'neutral' }
            };
            this.dashboardPeriods = {
                current: {},
                previous: {}
            };
            this.dashboardCheckErrors.stats = true;
            this.dashboardStatsCheckComplete = true;
        },
        applyDashboardData(dashboardData) {
            if (!dashboardData) {
                this.resetDashboardDataFallback();
                return;
            }

            this.dashboardCheckErrors.stats = Boolean(dashboardData.error);
            this.dashboardStatsCheckComplete = true;

            if (dashboardData.comparison) {
                this.statsComparison = dashboardData.comparison;
            }

            if (dashboardData.periods) {
                this.dashboardPeriods = dashboardData.periods;
            }

            if (dashboardData.dashboardStats && this.hasDashboardStatsChanged(dashboardData.dashboardStats)) {
                this.setData({ dashboardStats: dashboardData.dashboardStats });
            }
        },

        applyCustomDateRange(dates) {
            if (!dates.start || !dates.end) {
                return;
            }

            this.customDateRange = dates;
            this.dateRange = {
                startDate: dates.start,
                endDate: dates.end
            };

            this.selectedDateRange = 'custom';
            
            const activeRange = {
                startDate: dates.start,
                endDate: dates.end
            };

            // Update dashboard stats
            this.fetchDashboardData(activeRange);
            
            // Update all charts with the custom date range
            this.updateAllCharts('custom');
        },

        syncDashboardDateRange(now = new Date()) {
            const activeRange = resolveDashboardDateRange(this.selectedDateRange, this.customDateRange, now);
            this.dateRange = activeRange;
            if (this.selectedDateRange !== 'custom') {
                this.customDateRange = {
                    start: activeRange.startDate,
                    end: activeRange.endDate
                };
            }
            return activeRange;
        },

        async fetchDashboardData(capturedRange = null) {
            if (!this.canViewDashboard) return;
            const requestId = ++this.dashboardStatsRequestId;
            this.loading = true;
            this.dashboardStatsCheckComplete = false;
            this.dashboardCheckErrors.stats = false;
            
            try {
                const activeRange = capturedRange || this.syncDashboardDateRange();

                // Fetch dashboard stats with the current date range
                const dashboardData = await this.refreshDashboardStats({
                    startDate: activeRange.startDate,
                    endDate: activeRange.endDate,
                    comparisonPeriod: this.comparisonPeriod,
                    includePilotSales: this.includePilotSales
                });
                if (requestId !== this.dashboardStatsRequestId) return;

                // Handle the new response structure
                this.applyDashboardData(dashboardData);

                this.lastUpdatedTime = formatBusinessInstant(new Date(), { timeOnly: true });

            } catch (error) {
                if (requestId !== this.dashboardStatsRequestId) return;
                console.error('Error in fetchDashboardData:', error);
                
                // Initialize stats with default values to prevent errors
                this.resetDashboardDataFallback();
            } finally {
                if (requestId === this.dashboardStatsRequestId) {
                    this.loading = false;
                }
            }
        },

        refreshDashboard(silent = false) {
            if (!this.canViewDashboard) return;
            const requestId = ++this.dashboardStatsRequestId;
            const activeRange = this.syncDashboardDateRange();
            if (!silent) {
                this.loading = true;
            }
            this.dashboardStatsCheckComplete = false;
            this.dashboardCheckErrors.stats = false;
            
            this.refreshDashboardStats({
                dateRange: {
                    startDate: activeRange.startDate,
                    endDate: activeRange.endDate
                },
                comparisonPeriod: this.comparisonPeriod,
                includePilotSales: this.includePilotSales
            }).then((dashboardData) => {
                if (requestId !== this.dashboardStatsRequestId) return;
                this.applyDashboardData(dashboardData);
            }).catch((error) => {
                if (requestId !== this.dashboardStatsRequestId) return;
                console.error('Error refreshing dashboard:', error);
                this.resetDashboardDataFallback();
            }).finally(() => {
                if (requestId !== this.dashboardStatsRequestId) return;
                if (!silent) {
                    this.loading = false;
                }
                this.lastUpdatedTime = formatBusinessInstant(new Date(), { timeOnly: true });

                // Refresh charts
                this.updateAllCharts('custom', {
                    start: activeRange.startDate,
                    end: activeRange.endDate
                });
            });
        },

        toggleAutoRefresh() {
            this.autoRefreshEnabled = !this.autoRefreshEnabled;
            
            if (this.autoRefreshEnabled) {
                this.startAutoRefresh();
            } else {
                this.stopAutoRefresh();
            }
        },
        
        startAutoRefresh() {
            if (!this.canViewDashboard) return;
            // Clear any existing interval first
            this.stopAutoRefresh();
            
            // Set a new interval
            this.refreshInterval = setInterval(() => {
                this.refreshDashboard(true);
            }, 30000); // 30 seconds
        },
        
        stopAutoRefresh() {
            if (this.refreshInterval) {
                clearInterval(this.refreshInterval);
                this.refreshInterval = null;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.dashboard-container {
    width: 100%;
    
    &.dark {
        color: $darkTextColor;
    }
}

.dashboard-welcome {
    padding: 24px;
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;

    h1 { font-size: 24px; }
    p { margin-bottom: 0; }
}

.dark .dashboard-welcome {
    background: $darkCardBg;
    border-color: $darkBorderColor;
}

.dashboard-content {
    width: 100%;
    /* Use global page container gutters; avoid extra inner padding */
    padding: 0;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 12px; /* tighten rhythm */
    
    @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
    }
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 8px;
    }
}

.agco-health-section {
    margin-bottom: 12px;
}

.charts-section {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
    margin-bottom: 12px; /* tighten rhythm */
    
    .chart-container {
        background-color: $lightCardBg;
        border-radius: 8px;
        padding: 14px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.06);
        border: 1px solid $lightBorderColor;
        transition: all 0.2s ease-in-out;
        max-height: 400px;
        
        &:hover {
            box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
        }
        
        .chart-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
            
            h3 {
                font-size: 15px;
                font-weight: 600;
                color: $primaryColor;
                margin: 0;
            }
            
            .chart-controls {
                display: flex;
                gap: 6px;
            }
        }
        
        .dark & {
            background-color: $darkCardBg;
            border-color: $darkBorderColor;
            
            .chart-header h3 {
                color: $secondaryColorDark;
            }
        }
    }
}

.section-header {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 16px;
    color: $primaryColor;
    
    .dark & {
        color: $secondaryColorDark;
    }
}

.no-data-message {
    text-align: center;
    padding: 40px 0;
    color: $textColor;
    opacity: 0.7;
    font-size: 16px;
    
    .dark & {
        color: $darkTextColor;
    }
}

.sub-charts-section {
    display: grid;
    min-width: 0;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;

    .top-brand-chart {
        grid-column: 1 / -1;
    }
    
    @media (max-width: 992px) {
        grid-template-columns: minmax(0, 1fr);
        gap: 10px;

        .top-brand-chart {
            grid-column: auto;
        }
    }
}
</style>
